import { Component } from '@angular/core';
import { cloneDeep as _cloneDeep, isNil as _isNil } from 'lodash';
import { Observable } from 'rxjs/Observable';
import { of as RxOf } from 'rxjs/observable/of';

import { DateTimeUtils, LocalStorageUtils } from '@blcloud/bl-common';
import { ApplyPeriodCriteriaDiv, ApplyPeriodCriteriaDivArray } from '@blcloud/bl-datamodel/enum/bizcmn/apply-period-criteria-div';
import { MonthWeekDayDiv, MonthWeekDayDivArray } from '@blcloud/bl-datamodel/enum/bizcmn/month-week-day-div';
import { BizNegoStatus, BizNegoStatusArray } from '@blcloud/bl-datamodel/enum/deals/biz-nego-status';
import { AbstractBlModal, BlDialogService, ModalReason, ModalSize } from '@blcloud/bl-ng-ui-component';

import { SEARCH_CONDITION_STORAGE_KEY } from '../../recept-list/recept-list.define';
import { SearchTargetDateTypeArray } from '../../recept-list/recept-list.interface';
import {
  DEFAULT_MONTH_WEEK_DAY_DIV_END,
  DEFAULT_MONTH_WEEK_DAY_DIV_START,
  DEFAULT_SEARCH_DATE_RANGE_END_DAYS,
  DEFAULT_SEARCH_DATE_RANGE_START_DAYS,
  DEFAULT_SEARCH_TARGET_DATE_TYPE,
  IReceptSearchScreenActionSetting,
} from './recept-search-screen-action-setting-modal.define';

@Component({
  templateUrl: './recept-search-screen-action-setting-modal.component.html',
  styleUrls: ['./recept-search-screen-action-setting-modal.component.scss']
})
export class ReceptSearchScreenActionSettingModalComponent extends AbstractBlModal {

  /** 初期表示設定 */
  _screenActionSetting: IReceptSearchScreenActionSetting;

  /** ○日前から の入力オプション（0〜99） */
  searchDateRangeDaysAgoOption = { min: 0, max: 99, displayZero: true, minus: false };
  /** ○日後まで の入力オプション（0〜99） */
  searchDateRangeDaysLaterOption = { min: 0, max: 99, displayZero: true, minus: false };

  /** 検索対象日時の選択肢 */
  readonly SearchTargetDateTypeArray = SearchTargetDateTypeArray;
  /** 受付状況の選択肢（商談開始を除外） */
  readonly FilteredBizNegoStatusArray = BizNegoStatusArray.filter((item) => item.value !== BizNegoStatus.Start);

  /** 適用期間基準区分 */
  applyPeriodCriteriaDivItems = ApplyPeriodCriteriaDivArray.map(div => {
    return { code: div.value, name: div.text };
  });

  /** 月週日区分 */
  monthWeekDayDivItems = MonthWeekDayDivArray.map(div => {
    return { code: div.value, name: div.text };
  });


  applyStartPeriodCriteriaDiv: string;
  applyEndPeriodCriteriaDiv: string;
  applyStartNum: number;
  applyEndNum: number;
  /** 月週日区分（開始用） */
  monthWeekDayDivStart: string;
  /** 月週日区分（終了用） */
  monthWeekDayDivEnd: string;

   /** 適用開始期間基準が「本日」かどうかを返します。 */
   get isApplyStartPeriodToday() {
    return this.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today;
  }

  /** 適用終了期間基準が「本日」かどうかを返します。 */
  get isApplyEndPeriodToday() {
    return this.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today;
  }

  constructor(
    private dialogService: BlDialogService,
  ) {
    super();
  }

  /** @override */
  defaultOption() {
    return { size: ModalSize.Large };
  }

  /**
   * @override
   */
  getResults(): any {
    return this._screenActionSetting;
  }

  /**
   * @override
   */
  onShown() {
    this._screenActionSetting = _cloneDeep(this.data.screenActionSetting);
    if (this._screenActionSetting) {
      // 初期表示設定が存在する場合は、初期表示設定の値を使う
      if (_isNil(this._screenActionSetting.searchDateRangeStartDays)) {
        this._screenActionSetting.searchDateRangeStartDays = DEFAULT_SEARCH_DATE_RANGE_START_DAYS;
      }
      if (_isNil(this._screenActionSetting.searchDateRangeEndDays)) {
        this._screenActionSetting.searchDateRangeEndDays = DEFAULT_SEARCH_DATE_RANGE_END_DAYS;
      }
      if (_isNil(this._screenActionSetting.searchTargetDateType)) {
        this._screenActionSetting.searchTargetDateType = DEFAULT_SEARCH_TARGET_DATE_TYPE;
      }
      if (_isNil(this._screenActionSetting.bizNegoStatusList)) {
        this._screenActionSetting.bizNegoStatusList = [];
      }
    } else {
    // 初期表示設定が存在しない場合は、ローカルストレージの検索条件を基に初期値を設定する
      const savedCondition = LocalStorageUtils.get(SEARCH_CONDITION_STORAGE_KEY);

      const searchTargetDateTypeFromStorage =
        savedCondition && !_isNil(savedCondition.searchTargetDateType)
          ? savedCondition.searchTargetDateType
          : undefined;
      const bizNegoStatusListFromStorage =
        savedCondition && !_isNil(savedCondition.bizNegoStatusList)
          ? _cloneDeep(savedCondition.bizNegoStatusList)
          : undefined;

      this._screenActionSetting = {
        searchDateRangeStartDays: DEFAULT_SEARCH_DATE_RANGE_START_DAYS,
        searchDateRangeEndDays: DEFAULT_SEARCH_DATE_RANGE_END_DAYS,
        monthWeekDayDivStart: DEFAULT_MONTH_WEEK_DAY_DIV_START,
        monthWeekDayDivEnd: DEFAULT_MONTH_WEEK_DAY_DIV_END,
        searchTargetDateType:
          !_isNil(searchTargetDateTypeFromStorage) ? searchTargetDateTypeFromStorage : DEFAULT_SEARCH_TARGET_DATE_TYPE,
        bizNegoStatusList: !_isNil(bizNegoStatusListFromStorage) ? bizNegoStatusListFromStorage : [],
      };
    }

    if (this._screenActionSetting) {
      // 画面用プロパティに値を反映（UIだけ別プロパティで持つ）
      const startValue = this._screenActionSetting.searchDateRangeStartDays;
      const endValue = this._screenActionSetting.searchDateRangeEndDays;

      if (startValue === 0) {
        this.applyStartPeriodCriteriaDiv = ApplyPeriodCriteriaDiv.Today;
        this.applyStartNum = 0;
      } else if (startValue < 0) {
        this.applyStartPeriodCriteriaDiv = ApplyPeriodCriteriaDiv.PastFromToday;
        this.applyStartNum = Math.abs(startValue);
      } else {
        this.applyStartPeriodCriteriaDiv = ApplyPeriodCriteriaDiv.FutureFromToday;
        this.applyStartNum = startValue;
      }

      if (endValue === 0) {
        this.applyEndPeriodCriteriaDiv = ApplyPeriodCriteriaDiv.Today;
        this.applyEndNum = 0;
      } else if (endValue < 0) {
        this.applyEndPeriodCriteriaDiv = ApplyPeriodCriteriaDiv.PastFromToday;
        this.applyEndNum = Math.abs(endValue);
      } else {
        this.applyEndPeriodCriteriaDiv = ApplyPeriodCriteriaDiv.FutureFromToday;
        this.applyEndNum = endValue;
      }

      this.monthWeekDayDivStart =
        this._screenActionSetting.monthWeekDayDivStart && typeof this._screenActionSetting.monthWeekDayDivStart === 'string'
          ? this._screenActionSetting.monthWeekDayDivStart
          : DEFAULT_MONTH_WEEK_DAY_DIV_START;
      this.monthWeekDayDivEnd =
        this._screenActionSetting.monthWeekDayDivEnd && typeof this._screenActionSetting.monthWeekDayDivEnd === 'string'
          ? this._screenActionSetting.monthWeekDayDivEnd
          : DEFAULT_MONTH_WEEK_DAY_DIV_END;
    }
  }

  /**
   * 受付状況（初期値）変更イベントハンドラ
   */
  onChangeBizNegoStatusList(event: string[]) {
    if (this._screenActionSetting) {
      this._screenActionSetting.bizNegoStatusList = event;
    }
  }

  /**
   * @override
   */
  dismiss(reason: ModalReason) {
    if (reason === ModalReason.Done) {
      this.validate().subscribe(
        result => {
          if (result) {
            this.emitDismissEvent(reason);
          }
        }
      );
    } else {
      // 確定以外はそのまま通知
      this.emitDismissEvent(reason);
    }
  }

  // ================================================================
  // private

  /**
   * 入力検証します。
   * @returns 検証結果
   */
  private validate(): Observable<boolean> {
    if (this._screenActionSetting) {
      // UI用の値を設定本体に反映（型はそのままで UI だけ変更）
      this._screenActionSetting.monthWeekDayDivStart = this.monthWeekDayDivStart;
      this._screenActionSetting.monthWeekDayDivEnd = this.monthWeekDayDivEnd;

      // 適用開始
      let startOffset = 0;
      if (this.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today) {
        startOffset = 0;
      } else {
        const num = typeof this.applyStartNum === 'number' ? this.applyStartNum : 0;
        if (this.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.PastFromToday) {
          startOffset = -num;
        } else if (this.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.FutureFromToday) {
          startOffset = num;
        }
      }

      // 適用終了
      let endOffset = 0;
      if (this.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today) {
        endOffset = 0;
      } else {
        const num = typeof this.applyEndNum === 'number' ? this.applyEndNum : 0;
        if (this.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.PastFromToday) {
          endOffset = -num;
        } else if (this.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.FutureFromToday) {
          endOffset = num;
        }
      }

      this._screenActionSetting.searchDateRangeStartDays = startOffset;
      this._screenActionSetting.searchDateRangeEndDays = endOffset;

      const start = this._screenActionSetting.searchDateRangeStartDays;
      const end = this._screenActionSetting.searchDateRangeEndDays;
      if (typeof start !== 'number' || Math.abs(start) > 99) {
        this.dialogService.warn('「対象期間（開始）」に0〜99の範囲で値を指定してください。').subscribe((dialogRef) => dialogRef.hide());
        return RxOf(false);
      }
      if (typeof end !== 'number' || Math.abs(end) > 99) {
        this.dialogService.warn('「対象期間（終了）」に0〜99の範囲で値を指定してください。').subscribe((dialogRef) => dialogRef.hide());
        return RxOf(false);
      }

      // 開始日・終了日を算出して開始 > 終了 でないか、および1ヶ月以内かをチェック
      const baseDate = DateTimeUtils.today();
      const divStart = this.monthWeekDayDivStart && typeof this.monthWeekDayDivStart === 'string'
          ? this.monthWeekDayDivStart
          : DEFAULT_MONTH_WEEK_DAY_DIV_START;
      const divEnd = this.monthWeekDayDivEnd && typeof this.monthWeekDayDivEnd === 'string'
          ? this.monthWeekDayDivEnd
          : DEFAULT_MONTH_WEEK_DAY_DIV_END;
      const startDate = this.calculateDateFromOffset(baseDate, startOffset, divStart);
      const endDate = this.calculateDateFromOffset(baseDate, endOffset, divEnd);
      if (startDate > endDate) {
        this.dialogService
          .warn('対象期間が不正です。入力内容を確認してください。')
          .subscribe((dialogRef) => dialogRef.hide());
        return RxOf(false);
      }
      // 終了日基準で判定（開始日 = 終了日-1ヶ月 を1ヶ月以内として許容する）
      const endDateMinusOneMonth = DateTimeUtils.addMonths(endDate, -1);
      if (startDate < endDateMinusOneMonth) {
        this.dialogService
          .warn('対象期間は1ヶ月以内で指定してください。')
          .subscribe((dialogRef) => dialogRef.hide());
        return RxOf(false);
      }
    }
    return RxOf(true);
  }

  /**
   * 基準日とオフセット（月週日区分＋数値）から日付を算出する
   */
  private calculateDateFromOffset(baseDate: any, offset: number, monthWeekDayDiv: string): any {
    if (offset === 0) {
      return baseDate;
    }
    if (monthWeekDayDiv === MonthWeekDayDiv.Week) {
      return DateTimeUtils.addDays(baseDate, offset * 7);
    }
    if (monthWeekDayDiv === MonthWeekDayDiv.Month) {
      return DateTimeUtils.addMonths(baseDate, offset);
    }
    return DateTimeUtils.addDays(baseDate, offset);
  }
}



// WEBPACK FOOTER //
// ./src/app/page/recept-list-page/modal/recept-search-screen-action-setting-modal/recept-search-screen-action-setting-modal.component.ts